 import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ScrollView,
  Modal,
  TextInput,
  Pressable,
  Linking,
  Platform,
  AppState,
  Switch,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Calendar } from "react-native-calendars";
import notifee from "@notifee/react-native";
import { appConfig } from "./config/appConfig.js";

import {
  setupNotificationActions,
  syncDailyCheckInNotifications,
  scheduleAppLimitNotification,
} from "./utils/notifications";
import {
  CATEGORY_NAMES,
  explainUrlDecision,
  normalizeCategoryRules,
  parseBlockListText,
  parseKeywordList,
  safeOpenUrl,
} from "./utils/websiteBlocker";
import { suggestCategoryForDomain } from "./utils/aiCategoryHelper";
import { verifyPasscode } from "./utils/screenTimePasscode";
import {
  androidScreenTimeSupported,
  hasUsageStatsPermission,
  openUsageAccessSettings,
  showLimitOverlay,
  getTodayUsageMs,
  requestOverlayPermission,
  canDrawOverlays,
} from "./utils/androidScreenTime";
import { syncRules, startProtection, stopProtection } from "./platform/screenTimeBridge";
import { incrementBlocksToday, resetBlocksToday } from "./storage/blockStatsStorage";
import { ParentalFilterPanel } from "./components/ParentalFilterPanel";
import { DEFAULT_CATEGORY_RULES } from "./blocking/categoryModel";
import { mergeBlockedKeywordsWithAdvancedMedia } from "./blocking/advancedMediaKeywords";
import { getAdvancedMediaKeywordsEnabled, getGoogleYoutubeSafeModeEnabled } from "./blocking/BlocklistManager";
import { bootstrapLockboxEnforcement, useLockboxHydration } from "./lockbox";
import { AppLibraryScreen } from "./components/AppLibraryScreen";
import { LockboxLabPanel } from "./components/LockboxLabPanel";
import { FailTriggerPicker } from "./components/FailTriggerPicker";
import { BookRecommendationsScreen } from "./components/BookRecommendationsScreen";
import { StreakHistoryChart } from "./components/StreakHistoryChart";
import { buildStreakRuns, getTotalFailAttempts, streakDaysBeforeDate } from "./utils/streakHistory";
import { ScreenTimePasscodeGate } from "./components/ScreenTimePasscodeGate";
import { ScreenTimePasscodeSetup } from "./components/ScreenTimePasscodeSetup";
import { ScreenTimeHelpSession } from "./components/ScreenTimeHelpSession";
import { HelpCenterScreen } from "./components/HelpCenterScreen";
import { CommunityScreen } from "./components/CommunityScreen";
import { HomeHubScreen } from "./components/HomeHubScreen";
import { InsightsTabPanel } from "./components/InsightsTabPanel";
import { LibraryTabPanel } from "./components/LibraryTabPanel";
import { ScreenTimeTabPanel } from "./components/ScreenTimeTabPanel";
import { CommunityTabPanel } from "./components/CommunityTabPanel";
import { SubScreenShell } from "./components/SubScreenShell";
import { HabitSettingsScreen } from "./components/HabitSettingsScreen";
import { WelcomeIntroScreen } from "./components/WelcomeIntroScreen";
import { DailyQuoteScreen } from "./components/DailyQuoteScreen";
import { getBottomInset, getTopInset } from "./utils/safeInsets";
import { isScreenTimeFeaturesEnabled } from "./utils/featureFlags";
import { useScreenTimeEditGuard } from "./components/ScreenTimeEditGuard";
import { LIBRARY_ARTICLES } from "./content/libraryCatalog.js";
import { DAILY_QUOTES, normalizeStoredQuote } from "./content/dailyQuotes.js";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext.js";
import { MessagesScreen } from "./components/MessagesScreen";
import { handleNotificationResponseIfNeeded } from "./utils/notificationCheckIn";
import {
  addNotificationResponseReceivedListener,
  getLastNotificationResponseAsync,
  handleNotifeeEvent,
} from "./utils/notificationResponses.js";
import { getUnreadMessageCount, completeFailDetailsForDate } from "./storage/appMessagesStorage";
import {
  loadTriggerLibrary,
  mergeTriggersFromHistory,
  normalizeTriggerLabel,
  recordTriggerUsage,
  saveTriggerLibrary,
} from "./storage/triggerLibraryStorage";
import {
  loadCustomHabits,
  loadHabitLogs,
  newHabitId,
  pickHabitColor,
  saveCustomHabits,
  saveHabitLogs,
  setHabitCount,
  todayDateKey,
} from "./storage/customHabitsStorage";

const SCREEN_TIME_ON = isScreenTimeFeaturesEnabled();

function parseHmToMinutes(str) {
  if (typeof str !== "string") return null;
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(str.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function isInDowntimeWindow(startStr, endStr) {
  const start = parseHmToMinutes(startStr);
  const end = parseHmToMinutes(endStr);
  if (start == null || end == null || start === end) return false;
  const now = new Date();
  const current = now.getHours() * 60 + now.getMinutes();
  if (start < end) return current >= start && current < end;
  return current >= start || current < end;
}

const SCREEN_TIME_PROTECTED_PAGES = new Set(["settings", "parental", "applibrary", "lockboxlab"]);
const APP_TOP_INSET = getTopInset();
const APP_BOTTOM_INSET = getBottomInset();

function getScreenTimeLockLabel(page) {
  if (page === "parental") return "Parental Filter";
  if (page === "applibrary") return "App Library";
  if (page === "lockboxlab") return "Lockbox Lab";
  return "Screen Time";
}

export default function App() {
  return (
    <LanguageProvider>
      <AppRoot />
    </LanguageProvider>
  );
}

function AppRoot() {
  if (SCREEN_TIME_ON) {
    bootstrapLockboxEnforcement();
  }
  useLockboxHydration();
  const { t, locale } = useLanguage();

  const [markedDates, setMarkedDates] = useState({});
  const [currentStreak, setCurrentStreak] = useState(0);
  const [successOptionsVisible, setSuccessOptionsVisible] = useState(false);
  const [successOptionsDateKey, setSuccessOptionsDateKey] = useState(null);
  const [dayDetailsVisible, setDayDetailsVisible] = useState(false);
  const [dayDetailsDateKey, setDayDetailsDateKey] = useState(null);
  const [dayNoteDraft, setDayNoteDraft] = useState("");
  const [relapseModalVisible, setRelapseModalVisible] = useState(false);
  const [relapseDateKey, setRelapseDateKey] = useState(null);
  const [relapseTrigger, setRelapseTrigger] = useState("");
  const [relapseTimeOfDay, setRelapseTimeOfDay] = useState("Evening");
  const [relapseMood, setRelapseMood] = useState(3);
  const [relapseNoteDraft, setRelapseNoteDraft] = useState("");
  const [urgeModalVisible, setUrgeModalVisible] = useState(false);
  const [urgeSecondsLeft, setUrgeSecondsLeft] = useState(60);
  const urgeTimerRef = useRef(null);
  const [activePage, setActivePage] = useState("home"); // ... | "notifications"
  const [mainTabId, setMainTabId] = useState("home");
  const [hubResumeKey, setHubResumeKey] = useState(0);
  const [notificationBadgeCount, setNotificationBadgeCount] = useState(0);
  const [openFailFromNotifications, setOpenFailFromNotifications] = useState(false);
  const [startDatePickerVisible, setStartDatePickerVisible] = useState(false);

  const SETTINGS_STORAGE_KEY = "settings";
  const DEFAULT_SETTINGS = {
    habitName: "Procsystim",
    startDate: "", // YYYY-MM-DD
    goalDays: 90,
    reminderTime: "", // HH:MM
    dailyCheckInNotificationsEnabled: true,
    blockedSitesText: "", // newline or comma separated domains/URLs
    allowedSitesText: "",
    blockedKeywordsText: "",
    screenTimePasscodeHash: null,
    screenTimePasscodeSalt: null,
    screenTimePasscodeEncEscrow: null,
    screenTimePasscodeEncRecovery: null,
    screenTimeHelpEscrowSalt: null,
    screenTimeRecoveryEmail: null,
    screenTimeRecoveryHash: null,
    screenTimeRecoverySalt: null,
    screenTimeRecoverySkipped: false,
    /** @type {string} Android app package for usage-based limit (e.g. com.google.android.youtube) */
    androidLimitPackageName: "",
    /** @type {string} Daily foreground minutes before limit overlay */
    androidLimitMinutesDaily: "",
    /** @type {string} Downtime start HH:MM local */
    downtimeStart: "",
    /** @type {string} Downtime end HH:MM local (may cross midnight) */
    downtimeEnd: "",
    /** @type {Record<string, "allow"|"block"|"ignore">} */
    categoryRules: DEFAULT_CATEGORY_RULES,
  };
  const SOCIAL_BLOCK_PRESETS = [
    "facebook.com",
    "instagram.com",
    "twitter.com",
    "x.com",
    "tiktok.com",
    "youtube.com",
    "snapchat.com",
    "reddit.com",
    "pinterest.com",
    "linkedin.com",
    "threads.net",
  ];
  const [settingsDraft, setSettingsDraft] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [screenTimeSessionUnlocked, setScreenTimeSessionUnlocked] = useState(true);
  const [screenTimeUnlockInput, setScreenTimeUnlockInput] = useState("");
  const [screenTimeHelpVisible, setScreenTimeHelpVisible] = useState(false);
  const [passcodeSetupVisible, setPasscodeSetupVisible] = useState(false);
  const [pendingPasscodePin, setPendingPasscodePin] = useState("");
  const prevActivePageRef = useRef("home");
  const prevMainTabRef = useRef("home");
  /** When true, sub-screens (parental, lockbox, etc.) return to Screen Time tab */
  const screenTimeReturnRef = useRef(false);
  const [newPasscodeDraft, setNewPasscodeDraft] = useState("");
  const [confirmPasscodeDraft, setConfirmPasscodeDraft] = useState("");
  const [removePasscodeDraft, setRemovePasscodeDraft] = useState("");
  const [appLimitLabel, setAppLimitLabel] = useState("");
  const [appLimitMinutes, setAppLimitMinutes] = useState("30");
  const [ruleTestInput, setRuleTestInput] = useState("");
  const [ruleTestTitleInput, setRuleTestTitleInput] = useState("");
  const [ruleTestResult, setRuleTestResult] = useState("");
  const [ruleTestSuggestion, setRuleTestSuggestion] = useState("");
  const [customHabits, setCustomHabits] = useState([]);
  const [habitLogs, setHabitLogs] = useState({});
  const [savedTriggers, setSavedTriggers] = useState([]);

  const hasScreenTimePasscode = !!(settingsDraft.screenTimePasscodeHash && settingsDraft.screenTimePasscodeSalt);
  const { guardEdit: guardScreenTimeEdit, editPasscodeModal: screenTimeEditPasscodeModal } =
    useScreenTimeEditGuard({
      hasPasscode: hasScreenTimePasscode,
      passcodeSalt: settingsDraft.screenTimePasscodeSalt || "",
      passcodeHash: settingsDraft.screenTimePasscodeHash || "",
    });

  const guardSettingsDraft = (updater) => {
    guardScreenTimeEdit(() => {
      setSettingsDraft(updater);
    });
  };

  const SUCCESS_COLOR = "#1ABC9C";
  const PARTIAL_COLOR = "#76D7C4";
  const MISSED_COLOR = "#D1D5DB";
  const RELAPSE_COLOR = "#E74C3C";

  const MILESTONES_STORAGE_KEY = "milestonesShown";
  const MILESTONE_MESSAGES = {
    3: "3 days – Your brain is starting to rebalance dopamine. Early discipline builds real momentum.",
    7: "7 days – You may notice more energy, better focus, and stronger self‑control.",
    14: "14 days – Mental clarity is improving. You’re proving urges don’t control you.",
    30: "30 days – Confidence, motivation, and emotional stability often feel stronger by now.",
    90: "90 days – This is a major rewiring milestone. Your habits and mindset are on a new level.",
  };

  const [milestonesShown, setMilestonesShown] = useState({});
  const milestonesShownRef = useRef({});
  const androidNudgeRef = useRef({ limit: 0, down: 0 });
  const [milestoneToast, setMilestoneToast] = useState({
    visible: false,
    days: null,
    message: "",
  });

  const QUOTE_STORAGE_KEY = "quoteOfTheDay";
  const QUOTE_INTRO_SEEN_KEY = "quoteIntroSeen_v1";
  const QUOTE_DAY_SHOWN_KEY = "quoteDayShown_v1";
  const WELCOME_INTRO_SEEN_KEY = "welcomeIntroSeen_v2";
  const [quoteOfTheDay, setQuoteOfTheDay] = useState(null);
  const [showQuoteIntro, setShowQuoteIntro] = useState(false);
  const [welcomeIntroReady, setWelcomeIntroReady] = useState(false);
  const [showWelcomeIntro, setShowWelcomeIntro] = useState(false);
  const [showDailyQuote, setShowDailyQuote] = useState(false);

  const ARTICLES = LIBRARY_ARTICLES;
  const [selectedArticleId, setSelectedArticleId] = useState(null);

  const VALID_STATUSES = new Set(["success", "partial", "missed", "relapse"]);
  const getStatusColor = (status) => {
    if (status === "success") return SUCCESS_COLOR;
    if (status === "partial") return PARTIAL_COLOR;
    if (status === "missed") return MISSED_COLOR;
    if (status === "relapse") return RELAPSE_COLOR;
    return SUCCESS_COLOR;
  };

  const toDayIndex = (key) => {
    // key format: YYYY-MM-DD
    const [y, m, d] = String(key).split("-").map((v) => Number(v));
    if (!y || !m || !d) return null;
    return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
  };

  const getTodayKey = () => new Date().toISOString().split("T")[0];

  const inferStatusFromLegacyEntry = (entry) => {
    if (!entry || typeof entry !== "object") return "missed";
    if (VALID_STATUSES.has(entry.status)) return entry.status;
    if (entry.relapse || entry.selectedColor === RELAPSE_COLOR) return "relapse";
    if (entry.selectedColor === SUCCESS_COLOR) return "success";
    // For older unknown colors or note-only entries, default to missed.
    return "missed";
  };

  const withStatusCalendarStyling = (entry, status) => {
    const out = {
      ...entry,
      status,
      selected: true,
      selectedColor: getStatusColor(status),
      note: typeof entry?.note === "string" ? entry.note : "",
    };

    if (status === "relapse") {
      out.marked = true;
      out.dotColor = RELAPSE_COLOR;
    } else {
      if (out.relapse) delete out.relapse;
      if (out.marked) delete out.marked;
      if (out.dotColor) delete out.dotColor;
    }

    return out;
  };

  const normalizeSettings = (raw) => {
    const habitName =
      typeof raw?.habitName === "string" && raw.habitName.trim().length > 0
        ? raw.habitName.trim()
        : DEFAULT_SETTINGS.habitName;

    const startDate =
      typeof raw?.startDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.startDate)
        ? raw.startDate
        : DEFAULT_SETTINGS.startDate;

    const parsedGoal = Number(raw?.goalDays);
    const goalDays =
      Number.isFinite(parsedGoal) && parsedGoal > 0 ? Math.floor(parsedGoal) : DEFAULT_SETTINGS.goalDays;

    const reminderTime =
      typeof raw?.reminderTime === "string" && raw.reminderTime.trim().length > 0
        ? raw.reminderTime.trim()
        : DEFAULT_SETTINGS.reminderTime;

    const dailyCheckInNotificationsEnabled =
      typeof raw?.dailyCheckInNotificationsEnabled === "boolean"
        ? raw.dailyCheckInNotificationsEnabled
        : DEFAULT_SETTINGS.dailyCheckInNotificationsEnabled;

    const blockedSitesText =
      typeof raw?.blockedSitesText === "string" ? raw.blockedSitesText : DEFAULT_SETTINGS.blockedSitesText;

    const allowedSitesText =
      typeof raw?.allowedSitesText === "string" ? raw.allowedSitesText : DEFAULT_SETTINGS.allowedSitesText;

    const blockedKeywordsText =
      typeof raw?.blockedKeywordsText === "string" ? raw.blockedKeywordsText : DEFAULT_SETTINGS.blockedKeywordsText;

    const screenTimePasscodeHash =
      typeof raw?.screenTimePasscodeHash === "string" && raw.screenTimePasscodeHash.length > 0
        ? raw.screenTimePasscodeHash
        : null;

    const screenTimePasscodeSalt =
      typeof raw?.screenTimePasscodeSalt === "string" && raw.screenTimePasscodeSalt.length > 0
        ? raw.screenTimePasscodeSalt
        : null;

    const screenTimePasscodeEncEscrow =
      typeof raw?.screenTimePasscodeEncEscrow === "string" && raw.screenTimePasscodeEncEscrow.length > 0
        ? raw.screenTimePasscodeEncEscrow
        : null;

    const screenTimePasscodeEncRecovery =
      typeof raw?.screenTimePasscodeEncRecovery === "string" && raw.screenTimePasscodeEncRecovery.length > 0
        ? raw.screenTimePasscodeEncRecovery
        : null;

    const screenTimeHelpEscrowSalt =
      typeof raw?.screenTimeHelpEscrowSalt === "string" && raw.screenTimeHelpEscrowSalt.length > 0
        ? raw.screenTimeHelpEscrowSalt
        : null;

    const screenTimeRecoveryEmail =
      typeof raw?.screenTimeRecoveryEmail === "string" && raw.screenTimeRecoveryEmail.trim().length > 0
        ? raw.screenTimeRecoveryEmail.trim()
        : null;

    const screenTimeRecoveryHash =
      typeof raw?.screenTimeRecoveryHash === "string" && raw.screenTimeRecoveryHash.length > 0
        ? raw.screenTimeRecoveryHash
        : null;

    const screenTimeRecoverySalt =
      typeof raw?.screenTimeRecoverySalt === "string" && raw.screenTimeRecoverySalt.length > 0
        ? raw.screenTimeRecoverySalt
        : null;

    const screenTimeRecoverySkipped = !!raw?.screenTimeRecoverySkipped;

    const androidLimitPackageName =
      typeof raw?.androidLimitPackageName === "string" ? raw.androidLimitPackageName : DEFAULT_SETTINGS.androidLimitPackageName;
    const androidLimitMinutesDaily =
      typeof raw?.androidLimitMinutesDaily === "string" ? raw.androidLimitMinutesDaily : DEFAULT_SETTINGS.androidLimitMinutesDaily;
    const downtimeStart =
      typeof raw?.downtimeStart === "string" ? raw.downtimeStart : DEFAULT_SETTINGS.downtimeStart;
    const downtimeEnd =
      typeof raw?.downtimeEnd === "string" ? raw.downtimeEnd : DEFAULT_SETTINGS.downtimeEnd;
    const categoryRules = normalizeCategoryRules(raw?.categoryRules || DEFAULT_SETTINGS.categoryRules);

    return {
      habitName,
      startDate,
      goalDays,
      reminderTime,
      dailyCheckInNotificationsEnabled,
      blockedSitesText,
      allowedSitesText,
      blockedKeywordsText,
      screenTimePasscodeHash,
      screenTimePasscodeSalt,
      screenTimePasscodeEncEscrow,
      screenTimePasscodeEncRecovery,
      screenTimeHelpEscrowSalt,
      screenTimeRecoveryEmail,
      screenTimeRecoveryHash,
      screenTimeRecoverySalt,
      screenTimeRecoverySkipped,
      androidLimitPackageName,
      androidLimitMinutesDaily,
      downtimeStart,
      downtimeEnd,
      categoryRules,
    };
  };

  const loadSettings = async () => {
    try {
      const saved = await AsyncStorage.getItem(SETTINGS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setSettingsDraft(normalizeSettings(parsed));
      } else {
        setSettingsDraft((s) => ({ ...s, startDate: s.startDate || getTodayKey() }));
      }
    } catch (err) {
      console.log("Settings Load Error:", err);
      setSettingsDraft((s) => ({ ...s, startDate: s.startDate || getTodayKey() }));
    } finally {
      setSettingsLoaded(true);
    }
  };

  const saveSettings = async (next) => {
    const normalized = normalizeSettings(next);
    setSettingsDraft(normalized);
    try {
      await AsyncStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(normalized));
    } catch (err) {
      console.log("Settings Save Error:", err);
    }
    try {
      const [advancedMedia, safeMode] = await Promise.all([
        getAdvancedMediaKeywordsEnabled(),
        getGoogleYoutubeSafeModeEnabled(),
      ]);
      const keywords = mergeBlockedKeywordsWithAdvancedMedia(
        parseKeywordList(normalized.blockedKeywordsText),
        advancedMedia,
      );
      await syncRules(
        parseBlockListText(normalized.blockedSitesText),
        parseBlockListText(normalized.allowedSitesText),
        keywords,
        normalizeCategoryRules(normalized.categoryRules),
        { safeMode },
      );
    } catch (err) {
      console.log("Rules sync:", err);
    }
  };

  const dismissWelcomeIntro = async () => {
    try {
      await AsyncStorage.setItem(WELCOME_INTRO_SEEN_KEY, "true");
    } catch (err) {
      console.log("Welcome Intro Save Error:", err);
    }
    setShowWelcomeIntro(false);
    // After the mission intro, always show today's quote next.
    setShowDailyQuote(true);
  };

  const loadWelcomeIntro = async () => {
    try {
      const seen = (await AsyncStorage.getItem(WELCOME_INTRO_SEEN_KEY)) === "true";
      setShowWelcomeIntro(!seen);
      if (!seen) {
        // First launch: quote comes after "Begin your journey", not before.
        setShowDailyQuote(false);
        setShowQuoteIntro(false);
      } else {
        const today = getTodayKey();
        let dayShown = "";
        try {
          dayShown = (await AsyncStorage.getItem(QUOTE_DAY_SHOWN_KEY)) || "";
        } catch {
          dayShown = "";
        }
        setShowDailyQuote(dayShown !== today);
        setShowQuoteIntro(false);
      }
    } catch (err) {
      console.log("Welcome Intro Check Error:", err);
      setShowWelcomeIntro(true);
      setShowDailyQuote(false);
      setShowQuoteIntro(false);
    } finally {
      setWelcomeIntroReady(true);
    }
  };

  const loadQuoteOfTheDay = async () => {
    const today = getTodayKey();

    try {
      const saved = await AsyncStorage.getItem(QUOTE_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.date === today && parsed.quote) {
          const normalized = normalizeStoredQuote(parsed.quote);
          if (normalized) {
            setQuoteOfTheDay(normalized);
            return;
          }
        }
      }
    } catch (err) {
      console.log("Quote Load Error:", err);
    }

    const random = DAILY_QUOTES[Math.floor(Math.random() * DAILY_QUOTES.length)];
    setQuoteOfTheDay(random);
    try {
      await AsyncStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify({ date: today, quote: random }));
    } catch (err) {
      console.log("Quote Save Error:", err);
    }
  };

  const dismissDailyQuote = async () => {
    const today = getTodayKey();
    setShowDailyQuote(false);
    setShowQuoteIntro(false);
    try {
      await AsyncStorage.setItem(QUOTE_DAY_SHOWN_KEY, today);
      await AsyncStorage.setItem(QUOTE_INTRO_SEEN_KEY, "true");
      if (quoteOfTheDay) {
        await AsyncStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify({ date: today, quote: quoteOfTheDay }));
      }
    } catch (err) {
      console.log("Quote Day Shown Error:", err);
    }
  };

  const markQuoteShownForToday = async () => {
    await dismissDailyQuote();
  };

  const blockedList = parseBlockListText(settingsDraft.blockedSitesText);
  const allowedList = parseBlockListText(settingsDraft.allowedSitesText);
  const keywordBlockList = parseKeywordList(settingsDraft.blockedKeywordsText);
  const categoryRules = normalizeCategoryRules(settingsDraft.categoryRules);

  const onAndroidStartDnsVpn = async () => {
    try {
      const [advancedMedia, safeMode] = await Promise.all([
        getAdvancedMediaKeywordsEnabled(),
        getGoogleYoutubeSafeModeEnabled(),
      ]);
      const mergedKeywords = mergeBlockedKeywordsWithAdvancedMedia(keywordBlockList, advancedMedia);
      await syncRules(blockedList, allowedList, mergedKeywords, categoryRules, { safeMode });
      await startProtection();
      Alert.alert(
        "DNS filter",
        "Confirm the VPN connection if Android asks. Blocking uses only the lists in this app on your device — no browsing history is uploaded."
      );
    } catch (e) {
      Alert.alert("DNS filter", e?.message ? String(e.message) : String(e));
    }
  };

  const onAndroidStopDnsVpn = async () => {
    try {
      await stopProtection();
      Alert.alert("DNS filter", "Stopped.");
    } catch (e) {
      Alert.alert("DNS filter", e?.message ? String(e.message) : String(e));
    }
  };

  const mergeDomainsIntoBlockedText = (currentText, domains) => {
    const set = new Set(parseBlockListText(currentText));
    for (const line of domains) {
      parseBlockListText(line).forEach((d) => set.add(d));
    }
    return Array.from(set).join("\n");
  };

  const unlockScreenTime = async () => {
    const pin = screenTimeUnlockInput.trim();
    if (!pin) {
      Alert.alert("Passcode required", "Enter your screen time passcode.");
      return;
    }
    const ok = await verifyPasscode(
      pin,
      settingsDraft.screenTimePasscodeSalt,
      settingsDraft.screenTimePasscodeHash
    );
    if (!ok) {
      Alert.alert("Wrong passcode", "Try again.");
      return;
    }
    setScreenTimeUnlockInput("");
    setScreenTimeSessionUnlocked(true);
  };

  const applyNewScreenTimePasscode = () => {
    const a = newPasscodeDraft.trim();
    const b = confirmPasscodeDraft.trim();
    if (a.length < 4) {
      Alert.alert("Too short", "Use at least 4 characters.");
      return;
    }
    if (a !== b) {
      Alert.alert("Mismatch", "Passcodes do not match.");
      return;
    }
    setPendingPasscodePin(a);
    setPasscodeSetupVisible(true);
  };

  const completePasscodeSetup = async (patch) => {
    await saveSettings({
      ...settingsDraft,
      ...patch,
    });
    setNewPasscodeDraft("");
    setConfirmPasscodeDraft("");
    setPendingPasscodePin("");
    setPasscodeSetupVisible(false);
    Alert.alert(
      "Saved",
      "Screen time passcode now locks Screen Time, Parental Filter, App Library, and Lockbox Lab.",
    );
  };

  const removeScreenTimePasscode = async () => {
    const pin = removePasscodeDraft.trim();
    if (!pin) {
      Alert.alert("Passcode required", "Enter your current passcode to remove it.");
      return;
    }
    const ok = await verifyPasscode(
      pin,
      settingsDraft.screenTimePasscodeSalt,
      settingsDraft.screenTimePasscodeHash
    );
    if (!ok) {
      Alert.alert("Wrong passcode");
      return;
    }
    await saveSettings({
      ...settingsDraft,
      screenTimePasscodeHash: null,
      screenTimePasscodeSalt: null,
      screenTimePasscodeEncEscrow: null,
      screenTimePasscodeEncRecovery: null,
      screenTimeHelpEscrowSalt: null,
      screenTimeRecoveryEmail: null,
      screenTimeRecoveryHash: null,
      screenTimeRecoverySalt: null,
      screenTimeRecoverySkipped: false,
    });
    setRemovePasscodeDraft("");
    setScreenTimeSessionUnlocked(true);
    Alert.alert("Removed", "Screen time passcode has been removed.");
  };

  const onScheduleAppLimit = async () => {
    const label = appLimitLabel.trim() || "App";
    const mins = Number(appLimitMinutes);
    if (!Number.isFinite(mins) || mins < 1) {
      Alert.alert("Invalid time", "Enter minutes (1 or more).");
      return;
    }
    try {
      await scheduleAppLimitNotification(label, mins);
      Alert.alert(
        "Reminder scheduled",
        `You will get a notification in ${mins} minute(s). This does not close other apps — use iPhone Screen Time or Android Digital Wellbeing for hard limits.`
      );
    } catch (e) {
      console.warn(e);
      Alert.alert("Could not schedule", "Allow notifications for this app in system settings.");
    }
  };

  const setCategoryRule = (category, ruleValue) => {
    guardSettingsDraft((s) => ({
      ...s,
      categoryRules: normalizeCategoryRules({
        ...(s.categoryRules || DEFAULT_CATEGORY_RULES),
        [category]: ruleValue,
      }),
    }));
    if (ruleTestResult) setRuleTestResult("");
  };

  const onRunRuleTest = () => {
    const candidate = ruleTestInput.trim();
    if (!candidate) {
      Alert.alert("Missing input", "Enter a domain or URL to test.");
      return;
    }
    const suggested = suggestCategoryForDomain(candidate, ruleTestTitleInput);
    setRuleTestSuggestion(`Local suggestion (not enforced, no cloud): ${suggested}`);
    const decision = explainUrlDecision(candidate, blockedList, {
      allowList: allowedList,
      keywordBlockList,
      categoryRules,
    });
    if (decision.reason === "category-allow") {
      setRuleTestResult(`Allowed by category rule: ${decision.matchedRule}`);
      return;
    }
    if (decision.reason === "category-block") {
      setRuleTestResult(`Blocked by category rule: ${decision.matchedRule}`);
      return;
    }
    if (decision.reason === "allowlist") {
      setRuleTestResult(`Allowed by allowlist rule: ${decision.matchedRule}`);
      return;
    }
    if (decision.reason === "keyword") {
      setRuleTestResult(`Blocked by keyword rule: ${decision.matchedRule}`);
      return;
    }
    if (decision.reason === "blocklist") {
      setRuleTestResult(`Blocked by blocklist rule: ${decision.matchedRule}`);
      return;
    }
    setRuleTestResult("Allowed: no matching category/domain/keyword rule.");
  };

  const openExternalUrl = async (url) => {
    const advancedMedia = await getAdvancedMediaKeywordsEnabled();
    const mergedKeywords = mergeBlockedKeywordsWithAdvancedMedia(keywordBlockList, advancedMedia);
    return safeOpenUrl(url, {
      blockList: blockedList,
      allowList: allowedList,
      keywordBlockList: mergedKeywords,
      categoryRules,
      opener: async (u) => Linking.openURL(u),
      onBlocked: () => {
        incrementBlocksToday(1).catch(() => {});
        Alert.alert("Website Blocked", "This URL is blocked by your allow/block lists or keywords.");
      },
    });
  };

  const onResetPrivacyStats = async () => {
    try {
      await resetBlocksToday();
      Alert.alert("Done", "Local block counter reset.");
    } catch {
      Alert.alert("Error", "Could not reset local block counter.");
    }
  };

  const openFromScreenTime = (page) => {
    screenTimeReturnRef.current = true;
    setActivePage(page);
  };

  const backFromScreenTimeSub = () => {
    const returnToScreenTime = screenTimeReturnRef.current;
    screenTimeReturnRef.current = false;
    if (returnToScreenTime) {
      setMainTabId("screentime");
      setHubResumeKey((k) => k + 1);
    }
    setActivePage("home");
  };

  const onSaveScreenTimeTab = async () => {
    const rtRaw = (settingsDraft.reminderTime || "").trim();
    let rt = rtRaw;
    if (rtRaw) {
      const ampm = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(rtRaw);
      if (ampm) {
        let h = parseInt(ampm[1], 10);
        const m = ampm[2];
        const pm = ampm[3].toUpperCase() === "PM";
        if (pm && h < 12) h += 12;
        if (!pm && h === 12) h = 0;
        rt = `${String(h).padStart(2, "0")}:${m}`;
      }
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(rt)) {
        return { ok: false, message: "Please use HH:MM (e.g. 08:00)." };
      }
    }
    const gd = Number(settingsDraft.goalDays);
    if (!Number.isFinite(gd) || gd <= 0) {
      return { ok: false, message: "Goal days must be a positive number." };
    }
    const next = { ...settingsDraft, reminderTime: rt, goalDays: gd };
    await saveSettings(next);
    if (rt !== rtRaw) {
      setSettingsDraft((s) => ({ ...s, reminderTime: rt }));
    }
    return { ok: true, message: "Screen Time settings saved." };
  };

  const loadMilestonesShown = async () => {
    try {
      const saved = await AsyncStorage.getItem(MILESTONES_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const normalized = parsed && typeof parsed === "object" ? parsed : {};
        milestonesShownRef.current = normalized;
        setMilestonesShown(normalized);
      }
    } catch (err) {
      console.log("Milestones Load Error:", err);
      milestonesShownRef.current = {};
      setMilestonesShown({});
    }
  };

  const persistMilestonesShown = async (next) => {
    try {
      await AsyncStorage.setItem(MILESTONES_STORAGE_KEY, JSON.stringify(next));
    } catch (err) {
      console.log("Milestones Save Error:", err);
    }
  };

  const maybeShowMilestone = (streak) => {
    if (!Object.prototype.hasOwnProperty.call(MILESTONE_MESSAGES, streak)) return;

    const key = String(streak); 
    if (milestonesShownRef.current?.[key]) return;

    const message = MILESTONE_MESSAGES[streak];
    setMilestoneToast({ visible: true, days: streak, message });

    const next = { ...(milestonesShownRef.current ?? {}), [key]: true };
    milestonesShownRef.current = next;
    setMilestonesShown(next);
    persistMilestonesShown(next);
  };

  const dismissMilestoneToast = () => {
    setMilestoneToast({ visible: false, days: null, message: "" });
  };

  const getInsights = (dates) => {
    const keys = Object.keys(dates ?? {});
    const todayIndex = toDayIndex(getTodayKey());

    let successes = 0;
    let partials = 0;
    let missed = 0;
    let relapses = 0;
    let lastRelapseIndex = null;
    const successDayIndices = [];
    const relapseTimeCounts = {};
    const relapseTriggerCounts = {};
    const weekdayTotals = Array(7).fill(0);
    const weekdayWeightedSuccess = Array(7).fill(0);

    for (const key of keys) {
      const entry = dates?.[key];
      if (!entry || typeof entry !== "object") continue;

      const status = VALID_STATUSES.has(entry.status) ? entry.status : inferStatusFromLegacyEntry(entry);

      // Weekday stats (tracked days = any status present).
      const d = new Date(key);
      if (!Number.isNaN(d.getTime())) {
        const wd = d.getDay(); // 0-6
        weekdayTotals[wd] += 1;
        if (status === "success") weekdayWeightedSuccess[wd] += 1;
        else if (status === "partial") weekdayWeightedSuccess[wd] += 0.5;
      }

      if (status === "success") {
        successes += 1;
        const idx = toDayIndex(key);
        if (idx !== null) successDayIndices.push(idx);
      } else if (status === "partial") {
        partials += 1;
      } else if (status === "missed") {
        missed += 1;
      } else if (status === "relapse") {
        relapses += 1;
        const idx = toDayIndex(key);
        if (idx !== null) lastRelapseIndex = lastRelapseIndex === null ? idx : Math.max(lastRelapseIndex, idx);

        const bucket = entry.relapse?.timeOfDay || "Unknown";
        relapseTimeCounts[bucket] = (relapseTimeCounts[bucket] ?? 0) + 1;
        const trig = (entry.relapse?.trigger || "").trim();
        if (trig) {
          relapseTriggerCounts[trig] = (relapseTriggerCounts[trig] ?? 0) + 1;
        }
      }
    }

    // Best streak = max consecutive successes anywhere in history.
    const successSet = new Set(successDayIndices);
    let bestStreak = 0;
    for (const idx of successSet) {
      if (successSet.has(idx - 1)) continue; // not a streak start
      let run = 1;
      while (successSet.has(idx + run)) run += 1;
      bestStreak = Math.max(bestStreak, run);
    }

    const totalTracked = successes + partials + missed + relapses;
    const weightedSuccess = successes + partials * 0.5;
    const successRate = totalTracked === 0 ? 0 : Math.round((weightedSuccess / totalTracked) * 100);

    let mostCommonRelapseTimeOfDay = "None";
    let mostCommonCount = 0;
    for (const [bucket, count] of Object.entries(relapseTimeCounts)) {
      if (count > mostCommonCount) {
        mostCommonCount = count;
        mostCommonRelapseTimeOfDay = bucket;
      }
    }

    const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    let bestWeekday = null;
    let bestWeekdayRate = null;
    let worstWeekday = null;
    let worstWeekdayRate = null;

    for (let i = 0; i < 7; i++) {
      if (weekdayTotals[i] === 0) continue;
      const r = weekdayWeightedSuccess[i] / weekdayTotals[i];
      if (bestWeekdayRate === null || r > bestWeekdayRate) {
        bestWeekdayRate = r;
        bestWeekday = weekdayNames[i];
      }
      if (worstWeekdayRate === null || r < worstWeekdayRate) {
        worstWeekdayRate = r;
        worstWeekday = weekdayNames[i];
      }
    }

    const totalRelapses = relapses;
    const relapseTimeBreakdown = {};
    if (totalRelapses > 0) {
      const canonicalBuckets = ["Morning", "Afternoon", "Evening", "Night", "Unknown"];
      for (const bucket of canonicalBuckets) {
        const count = relapseTimeCounts[bucket] ?? 0;
        if (count > 0) {
          relapseTimeBreakdown[bucket] = Math.round((count / totalRelapses) * 100);
        }
      }
    }

    let mostCommonRelapseTrigger = null;
    let mostCommonTriggerCount = 0;
    for (const [trig, count] of Object.entries(relapseTriggerCounts)) {
      if (count > mostCommonTriggerCount) {
        mostCommonTriggerCount = count;
        mostCommonRelapseTrigger = trig;
      }
    }

    const hasWeekdayData = weekdayTotals.reduce((sum, v) => sum + v, 0) >= 5;
    const hasRelapsePatternData = totalRelapses >= 3;

    const daysSinceLastRelapse =
      todayIndex === null || lastRelapseIndex === null ? null : Math.max(0, todayIndex - lastRelapseIndex);

    return {
      currentStreak,
      bestStreak,
      daysSinceLastRelapse,
      successRate,
      mostCommonRelapseTimeOfDay,
      totalTracked,
       // New insight fields
      bestWeekday,
      bestWeekdayRate: bestWeekdayRate === null ? null : Math.round(bestWeekdayRate * 100),
      worstWeekday,
      worstWeekdayRate: worstWeekdayRate === null ? null : Math.round(worstWeekdayRate * 100),
      mostCommonRelapseTrigger,
      relapseTimeBreakdown,
      hasWeekdayData,
      hasRelapsePatternData,
    };
  };

  const normalizeMarkedDates = (data) => {
    if (!data || typeof data !== "object") return {};
    const out = {};
    for (const [key, entry] of Object.entries(data)) {
      if (!entry || typeof entry !== "object") {
        out[key] = entry;
        continue;
      }

      const status = inferStatusFromLegacyEntry(entry);
      out[key] = withStatusCalendarStyling(entry, status);
    }
    return out;
  };

  const loadHabitsData = async () => {
    try {
      const [habits, logs] = await Promise.all([loadCustomHabits(), loadHabitLogs()]);
      setCustomHabits(habits);
      setHabitLogs(logs);
    } catch (err) {
      console.log("Habits Load Error:", err);
    }
  };

  const persistHabitLogs = async (nextLogs) => {
    setHabitLogs(nextLogs);
    try {
      await saveHabitLogs(nextLogs);
    } catch (err) {
      console.log("Habits Save Error:", err);
    }
  };

  const handleHabitIncrement = (habitId) => {
    const today = todayDateKey();
    const current = habitLogs[today]?.[habitId] ?? 0;
    const next = setHabitCount(habitLogs, habitId, current + 1, today);
    void persistHabitLogs(next);
  };

  const handleHabitDecrement = (habitId) => {
    const today = todayDateKey();
    const current = habitLogs[today]?.[habitId] ?? 0;
    if (current <= 0) return;
    const next = setHabitCount(habitLogs, habitId, current - 1, today);
    void persistHabitLogs(next);
  };

  const handleAddCustomHabit = async ({ name, icon, dailyGoal }) => {
    const habit = {
      id: newHabitId(),
      name: name.trim().slice(0, 15),
      icon,
      dailyGoal: Math.max(1, dailyGoal || 1),
      color: pickHabitColor(customHabits),
      createdAt: new Date().toISOString(),
    };
    const next = [...customHabits, habit];
    setCustomHabits(next);
    try {
      await saveCustomHabits(next);
    } catch (err) {
      console.log("Habits Save Error:", err);
    }
  };

  const handleRemoveCustomHabit = async (habitId) => {
    const next = customHabits.filter((h) => h.id !== habitId);
    setCustomHabits(next);
    try {
      await saveCustomHabits(next);
    } catch (err) {
      console.log("Habits Save Error:", err);
    }
  };

  const refreshNotificationBadge = useCallback(async () => {
    try {
      const n = await getUnreadMessageCount();
      setNotificationBadgeCount(n);
    } catch {
      setNotificationBadgeCount(0);
    }
  }, []);

  const processNotificationResponse = useCallback(
    async (response) => {
      const result = await handleNotificationResponseIfNeeded(response);
      if (!result) return null;
      try {
        const saved = await AsyncStorage.getItem("markedDates");
        if (saved) {
          const parsed = JSON.parse(saved);
          const normalized = normalizeMarkedDates(parsed);
          setMarkedDates(normalized);
          calculateStreak(normalized);
        }
      } catch (err) {
        console.log("Notification reload error:", err);
      }
      await refreshNotificationBadge();
      return result;
    },
    [refreshNotificationBadge],
  );

  useEffect(() => {
    // Privacy migration: remove legacy per-attempt history key.
    AsyncStorage.removeItem("blockedAttempts").catch(() => {});
    loadData();
    loadHabitsData();
    loadSettings();
    loadMilestonesShown();
    loadWelcomeIntro();
    loadQuoteOfTheDay();
    refreshNotificationBadge();
    void setupNotificationActions();

    void getLastNotificationResponseAsync().then((response) => {
      void processNotificationResponse(response);
    });

    const subscription = addNotificationResponseReceivedListener((response) => {
      void processNotificationResponse(response);
    });

    const foregroundSub = notifee.onForegroundEvent(handleNotifeeEvent);

    const appStateSub = AppState.addEventListener("change", (state) => {
      if (state === "active") {
        void refreshNotificationBadge();
        void getLastNotificationResponseAsync().then((response) => {
          void processNotificationResponse(response);
        });
      }
    });

    return () => {
      subscription.remove();
      foregroundSub();
      appStateSub.remove();
    };
  }, [processNotificationResponse, refreshNotificationBadge]);

  useEffect(() => {
    if (!settingsLoaded) return;
    void syncDailyCheckInNotifications(!!settingsDraft.dailyCheckInNotificationsEnabled);
  }, [settingsLoaded, settingsDraft.dailyCheckInNotificationsEnabled]);

  const onToggleDailyCheckInNotifications = async (enabled) => {
    const next = { ...settingsDraft, dailyCheckInNotificationsEnabled: enabled };
    setSettingsDraft(next);
    if (enabled) {
      const ok = await syncDailyCheckInNotifications(true);
      if (!ok) {
        const off = { ...next, dailyCheckInNotificationsEnabled: false };
        setSettingsDraft(off);
        await saveSettings(off);
        Alert.alert(
          "Notifications off",
          "Allow notifications in system settings to use daily check-in reminders.",
        );
        return;
      }
    } else {
      await syncDailyCheckInNotifications(false);
    }
    await saveSettings(next);
  };

  useEffect(() => {
    const prevPage = prevActivePageRef.current;
    const prevTab = prevMainTabRef.current;
    prevActivePageRef.current = activePage;
    prevMainTabRef.current = mainTabId;

    const isProtectedContext = (page, tabId) =>
      SCREEN_TIME_PROTECTED_PAGES.has(page) || (page === "home" && tabId === "screentime");

    const wasProtected = isProtectedContext(prevPage, prevTab);
    const isProtected = isProtectedContext(activePage, mainTabId);

    if (!isProtected) {
      setScreenTimeUnlockInput("");
      if (settingsDraft.screenTimePasscodeHash) {
        setScreenTimeSessionUnlocked(false);
      }
      return;
    }

    if (!wasProtected && isProtected) {
      (async () => {
        try {
          const saved = await AsyncStorage.getItem(SETTINGS_STORAGE_KEY);
          if (!saved) {
            setScreenTimeSessionUnlocked(true);
            return;
          }
          const parsed = JSON.parse(saved);
          const n = normalizeSettings(parsed);
          if (n.screenTimePasscodeHash && n.screenTimePasscodeSalt) {
            setScreenTimeSessionUnlocked(false);
          } else {
            setScreenTimeSessionUnlocked(true);
          }
        } catch {
          setScreenTimeSessionUnlocked(true);
        }
      })();
    }
  }, [activePage, mainTabId, settingsDraft.screenTimePasscodeHash]);

  useEffect(() => {
    if (!SCREEN_TIME_ON) return undefined;
    if (Platform.OS !== "android" || !settingsLoaded || !androidScreenTimeSupported()) return undefined;
    const id = setInterval(async () => {
      const ds = settingsDraft.downtimeStart?.trim();
      const de = settingsDraft.downtimeEnd?.trim();
      if (ds && de && isInDowntimeWindow(ds, de)) {
        const now = Date.now();
        if (now - androidNudgeRef.current.down > 5 * 60 * 1000) {
          androidNudgeRef.current.down = now;
          showLimitOverlay("Blocked", "Downtime window active. This is a local reminder only (on-device).");
        }
      }
      const pkg = (settingsDraft.androidLimitPackageName || "").trim();
      const mins = Number(settingsDraft.androidLimitMinutesDaily);
      if (!pkg || !Number.isFinite(mins) || mins <= 0) return;
      const allowed = await hasUsageStatsPermission();
      if (!allowed) return;
      try {
        const ms = await getTodayUsageMs(pkg);
        if (ms >= mins * 60 * 1000) {
          const now = Date.now();
          if (now - androidNudgeRef.current.limit > 5 * 60 * 1000) {
            androidNudgeRef.current.limit = now;
            showLimitOverlay(
              "Blocked",
              `Daily app limit reached (usage stats, on-device). You reached about ${mins} minutes today for this package.`
            );
          }
        }
      } catch {
        /* ignore */
      }
    }, 60000);
    return () => clearInterval(id);
  }, [
    settingsLoaded,
    settingsDraft.downtimeStart,
    settingsDraft.downtimeEnd,
    settingsDraft.androidLimitPackageName,
    settingsDraft.androidLimitMinutesDaily,
  ]);

  useEffect(() => {
    return () => {
      if (urgeTimerRef.current) {
        clearInterval(urgeTimerRef.current);
        urgeTimerRef.current = null;
      }
    };
  }, []);

  const loadData = async () => {
    try {
      const saved = await AsyncStorage.getItem("markedDates");
      if (saved) {
        const parsed = JSON.parse(saved);
        const normalized = normalizeMarkedDates(parsed);
        setMarkedDates(normalized);
        calculateStreak(normalized);
        await syncTriggerLibrary(normalized);
      } else {
        await syncTriggerLibrary({});
      }
    } catch (err) {
      console.log("Load Error:", err);
    }
  };

  const syncTriggerLibrary = async (dates) => {
    try {
      let lib = await loadTriggerLibrary();
      lib = mergeTriggersFromHistory(lib, dates);
      await saveTriggerLibrary(lib);
      setSavedTriggers(lib);
    } catch (err) {
      console.log("Trigger library load error:", err);
    }
  };

  const saveData = async (data) => {
    try {
      await AsyncStorage.setItem("markedDates", JSON.stringify(data));
    } catch (err) {
      console.log("Save Error:", err);
    }
  };

  const markNonRelapseStatus = async (status, dateKey = getTodayKey(), noteOverride) => {
    const normalizedStatus = VALID_STATUSES.has(status) && status !== "relapse" ? status : "success";
    const noteToSave =
      typeof noteOverride === "string" ? noteOverride : (markedDates?.[dateKey]?.note ?? "");

    const prev = markedDates?.[dateKey] ?? {};
    const nextEntry = withStatusCalendarStyling({ ...prev, note: noteToSave }, normalizedStatus);

    const updated = {
      ...markedDates,
      [dateKey]: nextEntry,
    };

    setMarkedDates(updated);
    await saveData(updated);
    calculateStreak(updated);
  };

  const handleSuccessMark = async (dateKey = getTodayKey(), noteOverride) =>
    markNonRelapseStatus("success", dateKey, noteOverride);

  const handlePartialMark = async (dateKey = getTodayKey(), noteOverride) =>
    markNonRelapseStatus("partial", dateKey, noteOverride);

  const handleMissedMark = async (dateKey = getTodayKey(), noteOverride) =>
    markNonRelapseStatus("missed", dateKey, noteOverride);

  const openSuccessOptions = (dateKey = getTodayKey()) => {
    setSuccessOptionsDateKey(dateKey);
    setSuccessOptionsVisible(true);
  };

  const closeSuccessOptions = () => {
    setSuccessOptionsVisible(false);
    setSuccessOptionsDateKey(null);
  };

  const inferTimeOfDayFromNow = () => {
    const h = new Date().getHours();
    if (h < 12) return "Morning";
    if (h < 17) return "Afternoon";
    if (h < 21) return "Evening";
    return "Night";
  };

  const openRelapseModal = (dateKey = getTodayKey(), noteDraft) => {
    const existing = markedDates?.[dateKey]?.relapse;
    setRelapseDateKey(dateKey);
    setRelapseNoteDraft(
      typeof noteDraft === "string" ? noteDraft : (markedDates?.[dateKey]?.note ?? "")
    );
    setRelapseTrigger(existing?.trigger && !existing?.pendingDetails ? existing.trigger : "");
    setRelapseTimeOfDay(existing?.timeOfDay ?? inferTimeOfDayFromNow());
    setRelapseMood(
      typeof existing?.mood === "number" && existing.mood >= 1 && existing.mood <= 5
        ? existing.mood
        : 3
    );
    setRelapseModalVisible(true);
  };

  const openFailDetailsFromNotifications = (dateKey) => {
    setOpenFailFromNotifications(true);
    openRelapseModal(dateKey);
  };

  const closeRelapseModal = () => {
    setRelapseModalVisible(false);
    setRelapseDateKey(null);
    setRelapseTrigger("");
    setRelapseTimeOfDay("Evening");
    setRelapseMood(3);
    setRelapseNoteDraft("");
    setOpenFailFromNotifications(false);
  };

  const openDayDetails = (dateKey) => {
    const note = markedDates?.[dateKey]?.note ?? "";
    setDayDetailsDateKey(dateKey);
    setDayNoteDraft(note);
    setDayDetailsVisible(true);
  };

  const closeDayDetails = () => {
    setDayDetailsVisible(false);
    setDayDetailsDateKey(null);
    setDayNoteDraft("");
  };

  const saveDayNote = async () => {
    const dateKey = dayDetailsDateKey ?? getTodayKey();
    const existingStatus = markedDates?.[dateKey]?.status;
    const statusToSave = VALID_STATUSES.has(existingStatus) ? existingStatus : "missed";
    const updated = {
      ...markedDates,
      [dateKey]: {
        ...(markedDates[dateKey] ?? {}),
        note: dayNoteDraft,
        status: statusToSave,
        selected: true,
        selectedColor: getStatusColor(statusToSave),
      },
    };
    setMarkedDates(updated);
    await saveData(updated);
    closeDayDetails();
  };

  const openUrgeModal = () => {
    if (urgeTimerRef.current) {
      clearInterval(urgeTimerRef.current);
      urgeTimerRef.current = null;
    }

    setUrgeSecondsLeft(60);
    setUrgeModalVisible(true);

    urgeTimerRef.current = setInterval(() => {
      setUrgeSecondsLeft((s) => {
        if (s <= 1) {
          if (urgeTimerRef.current) {
            clearInterval(urgeTimerRef.current);
            urgeTimerRef.current = null;
          }
          setUrgeModalVisible(false);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const closeUrgeModal = () => {
    if (urgeTimerRef.current) {
      clearInterval(urgeTimerRef.current);
      urgeTimerRef.current = null;
    }
    setUrgeModalVisible(false);
  };

  const formatSeconds = (s) => {
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const saveRelapse = async () => {
    const dateKey = relapseDateKey ?? getTodayKey();
    const trigger = normalizeTriggerLabel(relapseTrigger);
    if (!trigger) {
      Alert.alert("Missing trigger", "Pick a trigger card or write what triggered the fail.");
      return;
    }

    const wasPendingDetails = !!markedDates?.[dateKey]?.relapse?.pendingDetails;
    const prev = markedDates?.[dateKey] ?? {};
    const nextEntry = withStatusCalendarStyling(
      {
        ...prev,
        note: relapseNoteDraft ?? (markedDates?.[dateKey]?.note ?? ""),
        relapse: {
          trigger,
          timeOfDay: relapseTimeOfDay,
          mood: relapseMood,
        },
      },
      "relapse"
    );

    const updated = {
      ...markedDates,
      [dateKey]: nextEntry,
    };

    setMarkedDates(updated);
    await saveData(updated);
    calculateStreak(updated);

    if (!wasPendingDetails) {
      const previousRunDays = streakDaysBeforeDate(markedDates, dateKey);
      Alert.alert(
        "Streak reset",
        previousRunDays > 0
          ? `Your ${previousRunDays}-day run is saved in Insights → Streak runs. Current streak is 0 until your next success.`
          : "Marked fail. Your next success day starts a new streak.",
      );
    } else {
      await completeFailDetailsForDate(dateKey);
      await refreshNotificationBadge();
    }

    try {
      const nextLib = await recordTriggerUsage(savedTriggers, trigger);
      setSavedTriggers(nextLib);
    } catch (err) {
      console.log("Trigger library save error:", err);
    }

    closeRelapseModal();
    if (openFailFromNotifications) {
      setActivePage("notifications");
    }
  };

  const calculateStreak = (dates) => {
    let streak = 0;
    let day = new Date();

    while (true) {
      const key = day.toISOString().split("T")[0];
      if (dates[key] && dates[key].status === "success") {
        streak++;
        day.setDate(day.getDate() - 1);
      } else break;
    }

    setCurrentStreak(streak);
    // Milestone system: show one-time toast when streak hits a milestone.
    maybeShowMilestone(streak);
  };

  const countSuccessDays = (days = 7) => {
    let score = 0;
    let day = new Date();
    for (let i = 0; i < days; i++) {
      const key = day.toISOString().split("T")[0];
      const status = markedDates?.[key]?.status;
      if (status === "success") score += 1;
      else if (status === "partial") score += 0.5;
      day.setDate(day.getDate() - 1);
    }
    return score;
  };

  const insights = getInsights(markedDates);
  const streakRuns = useMemo(() => buildStreakRuns(markedDates), [markedDates]);
  const totalAttempts = useMemo(() => getTotalFailAttempts(streakRuns), [streakRuns]);
  const needsScreenTimeUnlock =
    SCREEN_TIME_ON &&
    settingsLoaded &&
    settingsDraft.screenTimePasscodeHash &&
    !screenTimeSessionUnlocked;
  const screenTimeLockLabel =
    activePage === "home" && mainTabId === "screentime"
      ? "Screen Time"
      : getScreenTimeLockLabel(activePage);
  const isScreenTimeProtectedPage =
    SCREEN_TIME_ON &&
    (SCREEN_TIME_PROTECTED_PAGES.has(activePage) || (activePage === "home" && mainTabId === "screentime"));

  const onScreenTimeGateBack = () => {
    setScreenTimeUnlockInput("");
    setScreenTimeHelpVisible(false);
    setMainTabId("home");
    setActivePage("home");
  };

  const StatCard = ({ label, value, subvalue }) => (
    <View style={styles.statsCard}>
      <Text style={styles.statsLabel}>{label}</Text>
      <Text style={styles.statsValue}>{value}</Text>
      {subvalue ? <Text style={styles.statsSubvalue}>{subvalue}</Text> : null}
    </View>
  );

  const goalDaysSafe = Math.max(1, Number(settingsDraft.goalDays) || DEFAULT_SETTINGS.goalDays);
  const goalProgressPct = Math.min(100, Math.round((currentStreak / goalDaysSafe) * 100));

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    window.__PROCSYSTIM_TOUR__ = {
      setPage: (page) => setActivePage(page),
      setMainTab: (tabId) => {
        setActivePage("home");
        setMainTabId(tabId);
      },
      unlockScreenTime: () => setScreenTimeSessionUnlocked(true),
      dismissQuoteIntro: () => {
        void markQuoteShownForToday();
      },
      openRelapseModal: () => openRelapseModal(),
      closeRelapseModal: () => closeRelapseModal(),
      openUrgeModal: () => openUrgeModal(),
      closeUrgeModal: () => closeUrgeModal(),
      openScreenTimeHelp: () => setScreenTimeHelpVisible(true),
      closeScreenTimeHelp: () => setScreenTimeHelpVisible(false),
      openPasscodeSetup: () => {
        setPendingPasscodePin("1234");
        setPasscodeSetupVisible(true);
      },
      closePasscodeSetup: () => setPasscodeSetupVisible(false),
    };
    const params = new URLSearchParams(window.location.search);
    const autoPage = params.get("page");
    if (autoPage) setActivePage(autoPage);
  });

  const homeHub = (
    <HomeHubScreen
      habitName={settingsDraft.habitName || DEFAULT_SETTINGS.habitName}
      startDate={settingsDraft.startDate}
      currentStreak={currentStreak}
      bestStreak={insights.bestStreak}
      goalDays={goalDaysSafe}
      goalProgressPct={goalProgressPct}
      totalAttempts={totalAttempts}
      weeklyScore={countSuccessDays(7)}
      notificationBadgeCount={notificationBadgeCount}
      quoteOfTheDay={quoteOfTheDay}
      quoteIntroPending={false}
      markedDates={markedDates}
      customHabits={customHabits}
      habitLogs={habitLogs}
      showProtectionBar={SCREEN_TIME_ON && Platform.OS !== "web"}
      onNavigate={setActivePage}
      onMainTabChange={setMainTabId}
      activeTabId={mainTabId === "screentime" && !SCREEN_TIME_ON ? "home" : mainTabId}
      hubResumeKey={hubResumeKey}
      onMarkSuccess={() => openSuccessOptions()}
      onMarkFail={() => openRelapseModal()}
      onUrge={openUrgeModal}
      onDayPress={(day) => openDayDetails(day.dateString)}
      onQuoteSeen={markQuoteShownForToday}
      onHabitIncrement={handleHabitIncrement}
      onHabitDecrement={handleHabitDecrement}
      onAddHabit={handleAddCustomHabit}
      onRemoveHabit={handleRemoveCustomHabit}
      insightPanel={
        <InsightsTabPanel
          habitName={settingsDraft.habitName || DEFAULT_SETTINGS.habitName}
          insights={insights}
          markedDates={markedDates}
          totalAttempts={totalAttempts}
          currentStreak={currentStreak}
          goalDays={goalDaysSafe}
          goalProgressPct={goalProgressPct}
          streakRuns={streakRuns}
        />
      }
      libraryPanel={
        <LibraryTabPanel
          selectedArticleId={selectedArticleId}
          onSelectArticle={setSelectedArticleId}
        />
      }
      communityPanel={<CommunityTabPanel />}
      screenTimePanel={
        SCREEN_TIME_ON ? (
          <ScreenTimeTabPanel
            settingsLoaded={settingsLoaded}
            needsViewUnlock={needsScreenTimeUnlock}
            settingsDraft={settingsDraft}
            setSettingsDraft={setSettingsDraft}
            hasPasscode={hasScreenTimePasscode}
            passcodeSalt={settingsDraft.screenTimePasscodeSalt || ""}
            passcodeHash={settingsDraft.screenTimePasscodeHash || ""}
            onSave={onSaveScreenTimeTab}
            onToggleDailyCheckIn={onToggleDailyCheckInNotifications}
            onResetCounter={onResetPrivacyStats}
            onOpenParental={() => openFromScreenTime("parental")}
            onOpenLockboxLab={() => openFromScreenTime("lockboxlab")}
            onOpenAppLibrary={() => openFromScreenTime("applibrary")}
            onOpenAdvanced={() => openFromScreenTime("settings")}
          />
        ) : null
      }
    />
  );

  return (
    <>
      {!welcomeIntroReady ? (
        <View style={[styles.container, styles.containerHub, styles.containerFull]} />
      ) : showWelcomeIntro ? (
        <WelcomeIntroScreen onContinue={() => void dismissWelcomeIntro()} />
      ) : showDailyQuote ? (
        <DailyQuoteScreen quote={quoteOfTheDay} onContinue={() => void dismissDailyQuote()} />
      ) : activePage === "home" ? (
        <View style={[styles.container, styles.containerHub, styles.containerFull]}>{homeHub}</View>
      ) : activePage === "habitsettings" ? (
        <HabitSettingsScreen
          settingsDraft={settingsDraft}
          onChangeDraft={setSettingsDraft}
          onSave={saveSettings}
          onToggleDailyCheckIn={onToggleDailyCheckInNotifications}
          onBack={() => setActivePage("home")}
        />
      ) : SCREEN_TIME_ON &&
        (activePage === "parental" ||
          activePage === "applibrary" ||
          activePage === "lockboxlab" ||
          activePage === "settings") ? (
        needsScreenTimeUnlock ? null : activePage === "parental" ? (
          <SubScreenShell title="Parental Filter" onBack={backFromScreenTimeSub}>
            <Text style={styles.osStyleHint}>
              Like phone Screen Time: Chrome and browsers stay open. Adult sites are blocked. Google SafeSearch and
              YouTube Restricted Mode filter disturbing results. Only sites you add are fully blocked.
            </Text>
            <ParentalFilterPanel />
          </SubScreenShell>
        ) : activePage === "applibrary" ? (
          <SubScreenShell title="App Library" onBack={backFromScreenTimeSub} scroll={false} contentContainerStyle={{ flex: 1, paddingHorizontal: 12 }}>
            <AppLibraryScreen
              onBack={backFromScreenTimeSub}
              hideBack
              onOpenLockboxLab={() => openFromScreenTime("lockboxlab")}
            />
          </SubScreenShell>
        ) : activePage === "lockboxlab" ? (
          <SubScreenShell title="Lockbox Lab" onBack={backFromScreenTimeSub}>
            <LockboxLabPanel standalone />
          </SubScreenShell>
        ) : !settingsLoaded ? (
          <SubScreenShell title="Screen Time" onBack={backFromScreenTimeSub}>
            <Text style={styles.title}>Loading Screen Time…</Text>
          </SubScreenShell>
        ) : (
          <SubScreenShell title="Screen Time" onBack={backFromScreenTimeSub}>
              <Text style={styles.settingsInlineTitle}>
                {settingsDraft.habitName || DEFAULT_SETTINGS.habitName} • Advanced
              </Text>

              <View style={styles.statsContainer}>
                <Text style={styles.statsHeader}>Screen time & blocking</Text>
                <Text style={styles.screenTimeDisclaimer}>
                  Browser browsing stays available (Chrome, etc.). DNS filter blocks adult sites and anything you add
                  manually. Safe Mode enforces Google SafeSearch and YouTube Restricted Mode — YouTube still works,
                  with disturbing videos filtered. App blocking (TikTok, etc.) is Lockbox Lab, not website DNS.
                </Text>

                {Platform.OS === "android" && androidScreenTimeSupported() ? (
                  <View style={{ marginBottom: 16 }}>
                    <Text style={[styles.statsHeader, { marginTop: 4 }]}>Android: DNS filter (VPN)</Text>
                    <Text style={styles.screenTimeDisclaimer}>
                      Local DNS-only VPN: adult defaults + your block list + Safe Mode. Normal sites use your usual
                      internet. Start protection, then Allow the VPN prompt.
                    </Text>
                    <View style={styles.androidVpnButtonRow}>
                      <TouchableOpacity
                        style={[styles.actionButton, { backgroundColor: "#1ABC9C", flex: 1, marginRight: 6 }]}
                        onPress={() => guardScreenTimeEdit(onAndroidStartDnsVpn)}
                      >
                        <Text style={styles.buttonText}>Start DNS filter</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.actionButton, { backgroundColor: "#111", flex: 1, marginLeft: 6 }]}
                        onPress={() => guardScreenTimeEdit(onAndroidStopDnsVpn)}
                      >
                        <Text style={styles.buttonText}>Stop</Text>
                      </TouchableOpacity>
                    </View>
                    <TouchableOpacity
                      style={[styles.actionButton, { backgroundColor: "#6C5CE7", marginTop: 8 }]}
                      onPress={openUsageAccessSettings}
                    >
                      <Text style={styles.buttonText}>Open “Usage access” settings</Text>
                    </TouchableOpacity>
                    <Text style={styles.modalLabel}>App package for daily limit</Text>
                    <TextInput
                      value={settingsDraft.androidLimitPackageName}
                      onChangeText={(t) => guardSettingsDraft((s) => ({ ...s, androidLimitPackageName: t }))}
                      placeholder="e.g. com.google.android.youtube"
                      placeholderTextColor="#9AA0A6"
                      style={styles.modalInput}
                      autoCapitalize="none"
                      maxLength={200}
                    />
                    <Text style={styles.modalLabel}>Daily limit (minutes, foreground)</Text>
                    <TextInput
                      value={settingsDraft.androidLimitMinutesDaily}
                      onChangeText={(t) =>
                        guardSettingsDraft((s) => ({ ...s, androidLimitMinutesDaily: t.replace(/[^\d]/g, "") }))
                      }
                      placeholder="60"
                      placeholderTextColor="#9AA0A6"
                      style={styles.modalInput}
                      keyboardType="number-pad"
                      maxLength={4}
                    />
                    <Text style={styles.modalLabel}>Downtime (local time, HH:MM)</Text>
                    <TextInput
                      value={settingsDraft.downtimeStart}
                      onChangeText={(t) => guardSettingsDraft((s) => ({ ...s, downtimeStart: t }))}
                      placeholder="Start e.g. 22:00"
                      placeholderTextColor="#9AA0A6"
                      style={styles.modalInput}
                      maxLength={5}
                    />
                    <TextInput
                      value={settingsDraft.downtimeEnd}
                      onChangeText={(t) => guardSettingsDraft((s) => ({ ...s, downtimeEnd: t }))}
                      placeholder="End e.g. 07:00 (overnight OK)"
                      placeholderTextColor="#9AA0A6"
                      style={[styles.modalInput, { marginTop: 8 }]}
                      maxLength={5}
                    />
                    <TouchableOpacity
                      style={[styles.actionButton, { backgroundColor: "#34495E", marginTop: 8 }]}
                      onPress={async () => {
                        const ok = await canDrawOverlays();
                        if (ok) {
                          showLimitOverlay("Overlay test", "If you see this, full-screen limit prompts can show.");
                        } else {
                          requestOverlayPermission();
                          Alert.alert("Permission", "Allow display over other apps if you want full-screen prompts.");
                        }
                      }}
                    >
                      <Text style={styles.buttonText}>Test limit overlay</Text>
                    </TouchableOpacity>
                  </View>
                ) : null}

                <Text style={styles.modalLabel}>Always allow (domains, one per line)</Text>
                <TextInput
                  value={settingsDraft.allowedSitesText}
                  onChangeText={(t) => guardSettingsDraft((s) => ({ ...s, allowedSitesText: t }))}
                  placeholder={"school.edu\ntrusted-site.org"}
                  placeholderTextColor="#9AA0A6"
                  style={[styles.modalInput, styles.noteInput]}
                  multiline
                  maxLength={4000}
                />

                <Text style={styles.modalLabel}>Blocked websites (one per line)</Text>
                <TextInput
                  value={settingsDraft.blockedSitesText}
                  onChangeText={(t) => guardSettingsDraft((s) => ({ ...s, blockedSitesText: t }))}
                  placeholder={"example.com\nbad.example.org"}
                  placeholderTextColor="#9AA0A6"
                  style={[styles.modalInput, styles.noteInput]}
                  multiline
                  maxLength={4000}
                />

                <Text style={styles.modalLabel}>Block URLs containing keywords</Text>
                <TextInput
                  value={settingsDraft.blockedKeywordsText}
                  onChangeText={(t) => guardSettingsDraft((s) => ({ ...s, blockedKeywordsText: t }))}
                  placeholder={"One keyword per line (matched in full URL)"}
                  placeholderTextColor="#9AA0A6"
                  style={[styles.modalInput, styles.noteInput]}
                  multiline
                  maxLength={4000}
                />

                <Text style={styles.modalLabel}>Category rules (domain → category → rule)</Text>
                <Text style={styles.screenTimeDisclaimer}>
                  Categories: Adult (default block), Gambling / Social Media / Gaming (default ignore). Add sites like youtube.com under Parental Filter to block them in the browser. Category rules are checked before allow/block domain lists.
                </Text>
                {CATEGORY_NAMES.map((category) => {
                  const current = normalizeCategoryRules(settingsDraft.categoryRules)[category];
                  return (
                    <View key={category} style={styles.categoryRuleRow}>
                      <Text style={styles.categoryRuleName}>{category}</Text>
                      <View style={styles.chipRow}>
                        {["ignore", "allow", "block"].map((rule) => {
                          const selected = current === rule;
                          return (
                            <TouchableOpacity
                              key={`${category}-${rule}`}
                              style={[
                                styles.chip,
                                selected ? styles.chipSelected : null,
                                rule === "block" && selected ? styles.chipDangerSelected : null,
                              ]}
                              onPress={() => setCategoryRule(category, rule)}
                            >
                              <Text
                                style={[
                                  styles.chipText,
                                  selected ? styles.chipTextSelected : null,
                                  rule === "block" && selected ? styles.chipDangerTextSelected : null,
                                ]}
                              >
                                {rule}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    </View>
                  );
                })}

                <Text style={styles.modalLabel}>Test a domain/URL against rules</Text>
                <Text style={styles.screenTimeDisclaimer}>
                  Optional suggestion uses only domain + optional title locally. No full-page scanning, no content
                  extraction, no cloud AI.
                </Text>
                <TextInput
                  value={ruleTestInput}
                  onChangeText={(t) => {
                    setRuleTestInput(t);
                    if (ruleTestResult) setRuleTestResult("");
                    if (ruleTestSuggestion) setRuleTestSuggestion("");
                  }}
                  placeholder="e.g. https://m.example-adult-site.com/path"
                  placeholderTextColor="#9AA0A6"
                  style={styles.modalInput}
                  autoCapitalize="none"
                  autoCorrect={false}
                  maxLength={300}
                />
                <TextInput
                  value={ruleTestTitleInput}
                  onChangeText={(t) => {
                    setRuleTestTitleInput(t);
                    if (ruleTestSuggestion) setRuleTestSuggestion("");
                  }}
                  placeholder="Optional page title for safer suggestion"
                  placeholderTextColor="#9AA0A6"
                  style={[styles.modalInput, { marginTop: 8 }]}
                  autoCapitalize="none"
                  autoCorrect={false}
                  maxLength={300}
                />
                <TouchableOpacity
                  style={[styles.actionButton, { backgroundColor: "#111", marginTop: 8 }]}
                  onPress={onRunRuleTest}
                >
                  <Text style={styles.buttonText}>Test rules</Text>
                </TouchableOpacity>
                {ruleTestResult ? <Text style={styles.ruleTestResultText}>{ruleTestResult}</Text> : null}
                {ruleTestSuggestion ? (
                  <Text style={styles.ruleTestSuggestionText}>{ruleTestSuggestion}</Text>
                ) : null}

                <Text style={styles.modalLabel}>Quick add: social & video</Text>
                <View style={styles.chipRow}>
                  <TouchableOpacity
                    style={styles.chip}
                    onPress={() =>
                      guardSettingsDraft((s) => ({
                        ...s,
                        blockedSitesText: mergeDomainsIntoBlockedText(s.blockedSitesText, SOCIAL_BLOCK_PRESETS),
                      }))
                    }
                  >
                    <Text style={styles.chipText}>Add common social sites</Text>
                  </TouchableOpacity>
                </View>

                <Text style={styles.modalLabel}>App time reminder (any label)</Text>
                <Text style={styles.screenTimeDisclaimer}>
                  Schedules a notification when time is up. This cannot force-close apps — use OS Screen Time for
                  hard blocks.
                </Text>
                <TextInput
                  value={appLimitLabel}
                  onChangeText={setAppLimitLabel}
                  placeholder="e.g. YouTube, Instagram"
                  placeholderTextColor="#9AA0A6"
                  style={styles.modalInput}
                  maxLength={80}
                />
                <Text style={styles.modalLabel}>Minutes until reminder</Text>
                <TextInput
                  value={appLimitMinutes}
                  onChangeText={(t) => setAppLimitMinutes(t.replace(/[^\d]/g, ""))}
                  placeholder="30"
                  placeholderTextColor="#9AA0A6"
                  style={styles.modalInput}
                  keyboardType="number-pad"
                  maxLength={4}
                />
                <TouchableOpacity
                  style={[styles.actionButton, { backgroundColor: "#6C5CE7", marginTop: 8 }]}
                  onPress={() => guardScreenTimeEdit(onScheduleAppLimit)}
                >
                  <Text style={styles.buttonText}>Schedule reminder</Text>
                </TouchableOpacity>

                <Text style={styles.modalLabel}>Lock Screen Time with passcode</Text>
                <Text style={styles.screenTimeDisclaimer}>
                  Locks Screen Time, Parental Filter, App Library, and Lockbox Lab. Plain PIN is never stored — only a
                  hash plus encrypted copies for recovery or Help reveal.
                </Text>
                {!settingsDraft.screenTimePasscodeHash ? (
                  <>
                    <TextInput
                      value={newPasscodeDraft}
                      onChangeText={setNewPasscodeDraft}
                      placeholder="New passcode (min 4 chars)"
                      placeholderTextColor="#9AA0A6"
                      style={styles.modalInput}
                      secureTextEntry
                      maxLength={12}
                    />
                    <TextInput
                      value={confirmPasscodeDraft}
                      onChangeText={setConfirmPasscodeDraft}
                      placeholder="Confirm passcode"
                      placeholderTextColor="#9AA0A6"
                      style={[styles.modalInput, { marginTop: 8 }]}
                      secureTextEntry
                      maxLength={12}
                    />
                    <TouchableOpacity
                      style={[styles.actionButton, { backgroundColor: "#111", marginTop: 8 }]}
                      onPress={applyNewScreenTimePasscode}
                    >
                      <Text style={styles.buttonText}>Set screen time passcode</Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <>
                    <Text style={styles.screenTimeDisclaimer}>
                      {settingsDraft.screenTimeRecoverySkipped
                        ? "Recovery skipped — use Help (Forgot passcode) with habit name + best streak if needed."
                        : settingsDraft.screenTimeRecoveryEmail
                          ? `Recovery email: ${settingsDraft.screenTimeRecoveryEmail}`
                          : "Recovery not configured."}
                    </Text>
                    <TextInput
                      value={removePasscodeDraft}
                      onChangeText={setRemovePasscodeDraft}
                      placeholder="Current passcode to remove lock"
                      placeholderTextColor="#9AA0A6"
                      style={styles.modalInput}
                      secureTextEntry
                      maxLength={12}
                    />
                    <TouchableOpacity
                      style={[styles.actionButton, { backgroundColor: "#E74C3C", marginTop: 8 }]}
                      onPress={removeScreenTimePasscode}
                    >
                      <Text style={styles.buttonText}>Remove passcode</Text>
                    </TouchableOpacity>
                  </>
                )}

                <Text style={[styles.statsHeader, { marginTop: 18 }]}>Habit</Text>

                <Text style={styles.modalLabel}>Habit name</Text>
                <TextInput
                  value={settingsDraft.habitName}
                  onChangeText={(t) => guardSettingsDraft((s) => ({ ...s, habitName: t }))}
                  placeholder="Procsystim"
                  placeholderTextColor="#9AA0A6"
                  style={styles.modalInput}
                  maxLength={50}
                />

            <Text style={styles.modalLabel}>Start date</Text>
            <TouchableOpacity
              style={[styles.modalInput, styles.pickerRow]}
              onPress={() => guardScreenTimeEdit(() => setStartDatePickerVisible(true))}
            >
              <Text style={styles.pickerRowText}>{settingsDraft.startDate || "Pick a date"}</Text>
            </TouchableOpacity>

            <Text style={styles.modalLabel}>Goal days</Text>
            <TextInput
              value={String(settingsDraft.goalDays ?? DEFAULT_SETTINGS.goalDays)}
              onChangeText={(t) =>
                guardSettingsDraft((s) => ({
                  ...s,
                  goalDays: t === "" ? "" : Number(t.replace(/[^\d]/g, "")),
                }))
              }
              placeholder="90"
              placeholderTextColor="#9AA0A6"
              style={styles.modalInput}
              keyboardType="number-pad"
            />

            <Text style={styles.modalLabel}>Reminder time (HH:MM)</Text>
            <TextInput
              value={settingsDraft.reminderTime}
              onChangeText={(t) => guardSettingsDraft((s) => ({ ...s, reminderTime: t }))}
              placeholder="08:00"
              placeholderTextColor="#9AA0A6"
              style={styles.modalInput}
              maxLength={5}
            />

            <View style={styles.settingsSwitchRow}>
              <View style={styles.settingsSwitchCopy}>
                <Text style={styles.modalLabel}>Daily check-in notifications</Text>
                <Text style={styles.screenTimeDisclaimer}>
                  Morning (8:00) and evening (20:00) reminders with Mark today as success and Mark fail buttons on the
                  lock screen. Turn off anytime — no check-ins will be scheduled.
                </Text>
              </View>
              <Switch
                value={!!settingsDraft.dailyCheckInNotificationsEnabled}
                onValueChange={(v) => guardScreenTimeEdit(() => void onToggleDailyCheckInNotifications(v))}
                trackColor={{ false: "#D1D5DB", true: "#6EE7B7" }}
                thumbColor={settingsDraft.dailyCheckInNotificationsEnabled ? "#1ABC9C" : "#f4f3f4"}
              />
            </View>

            <View style={styles.blockedAttemptsHeaderRow}>
              <Text style={styles.modalLabel}>Privacy-first block stats</Text>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonSecondary, styles.blockedAttemptsButton]}
                onPress={() => guardScreenTimeEdit(() => void onResetPrivacyStats())}
              >
                <Text style={styles.modalButtonSecondaryText}>Reset counter</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.screenTimeDisclaimer}>
              Privacy mode: no blocked URL/domain history is stored. Only aggregated local counters are kept.
            </Text>
            <Text style={styles.statsSubvalue}>
              See aggregated "Blocks today" in the protection bar. No per-site logs are retained.
            </Text>
          </View>

          <View style={styles.modalActions}>
            <TouchableOpacity
              style={[styles.modalButton, styles.modalButtonPrimary]}
              onPress={() =>
                guardScreenTimeEdit(async () => {
                const rt = (settingsDraft.reminderTime || "").trim();
                if (rt && !/^([01]\d|2[0-3]):[0-5]\d$/.test(rt)) {
                  Alert.alert("Invalid time", "Please use HH:MM (e.g. 08:00).");
                  return;
                }
                const gd = Number(settingsDraft.goalDays);
                if (!Number.isFinite(gd) || gd <= 0) {
                  Alert.alert("Invalid goal", "Goal days must be a positive number.");
                  return;
                }
                await saveSettings({ ...settingsDraft, reminderTime: rt, goalDays: gd });
                Alert.alert("Saved", "Screen Time updated.");
                })
              }
            >
              <Text style={styles.modalButtonPrimaryText}>Save</Text>
            </TouchableOpacity>
          </View>

          <Modal
            visible={startDatePickerVisible}
            animationType="fade"
            transparent={true}
            onRequestClose={() => setStartDatePickerVisible(false)}
          >
            <View style={styles.modalBackdrop}>
              <View style={styles.modalCard}>
                <Text style={styles.modalTitle}>Pick a start date</Text>
                <Calendar
                  markedDates={
                    settingsDraft.startDate
                      ? { [settingsDraft.startDate]: { selected: true, selectedColor: SUCCESS_COLOR } }
                      : {}
                  }
                  onDayPress={(day) => {
                    guardSettingsDraft((s) => ({ ...s, startDate: day.dateString }));
                    setStartDatePickerVisible(false);
                  }}
                  theme={{
                    todayTextColor: SUCCESS_COLOR,
                    arrowColor: SUCCESS_COLOR,
                    selectedDayBackgroundColor: SUCCESS_COLOR,
                    monthTextColor: SUCCESS_COLOR,
                  }}
                />

                <View style={styles.modalActions}>
                  <TouchableOpacity
                    style={[styles.modalButton, styles.modalButtonSecondary]}
                    onPress={() => setStartDatePickerVisible(false)}
                  >
                    <Text style={styles.modalButtonSecondaryText}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>
          </SubScreenShell>
        )
      ) : (
        <ScrollView
          style={[styles.container, { paddingTop: APP_TOP_INSET, paddingBottom: APP_BOTTOM_INSET }]}
          contentContainerStyle={{ paddingBottom: 24 }}
          keyboardShouldPersistTaps="handled"
        >
          {activePage === "books" ? (
            <>
              <BookRecommendationsScreen onBack={() => setActivePage("home")} />
            </>
          ) : activePage === "help" ? (
            <>
              <HelpCenterScreen
                onBack={() => setActivePage("home")}
                habitName={settingsDraft.habitName}
                currentStreak={currentStreak}
              />
            </>
          ) : activePage === "notifications" ? (
            <>
              <MessagesScreen
                onBack={() => setActivePage("home")}
                onOpenFailDetails={openFailDetailsFromNotifications}
                onRefreshBadge={refreshNotificationBadge}
              />
            </>
          ) : activePage === "community" ? (
            <>
              <CommunityScreen onBack={() => setActivePage("home")} />
            </>
          ) : activePage === "insights" ? (
            <>
              <Text style={styles.title}>
                {settingsDraft.habitName || DEFAULT_SETTINGS.habitName} • Insights
              </Text>
              <Text style={styles.modalSubtitle}>
                Based on {insights.totalTracked} tracked day{insights.totalTracked === 1 ? "" : "s"}.
              </Text>
              <View style={styles.statsContainer}>
                <View style={styles.statsRow}>
                  <StatCard label="Current streak" value={`${insights.currentStreak} days`} />
                  <StatCard label="Best streak" value={`${insights.bestStreak} days`} />
                </View>
                <View style={styles.statsRow}>
                  <StatCard label="Total attempts" value={`${totalAttempts}`} subvalue="runs ended by fail" />
                  <StatCard
                    label="Days since last fail"
                    value={
                      insights.daysSinceLastRelapse === null
                        ? "N/A"
                        : `${insights.daysSinceLastRelapse} day${insights.daysSinceLastRelapse === 1 ? "" : "s"}`
                    }
                  />
                </View>
                <StreakHistoryChart runs={streakRuns} />
                <View style={styles.statsRow}>
                  <StatCard label="Success rate" value={`${insights.successRate}%`} subvalue="(success / tracked)" />
                </View>
              </View>
              <TouchableOpacity
                style={[styles.actionButton, { backgroundColor: "#111" }]}
                onPress={() => setActivePage("home")}
              >
                <Text style={styles.buttonText}>Back</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
          <Text style={styles.title}>
            {settingsDraft.habitName || DEFAULT_SETTINGS.habitName} • Articles
          </Text>

          {selectedArticleId
            ? (() => {
                const article = ARTICLES.find((a) => a.id === selectedArticleId);
                if (!article) return null;
                return (
                  <View style={styles.statsContainer}>
                    <Text style={styles.statsHeader}>{article.title}</Text>
                    <Text style={styles.articleBody}>{article.body}</Text>

                    <TouchableOpacity
                      style={[styles.actionButton, { backgroundColor: "#111" }]}
                      onPress={() => setSelectedArticleId(null)}
                    >
                      <Text style={styles.buttonText}>Back to articles</Text>
                    </TouchableOpacity>
                  </View>
                );
              })()
            : (
              <View style={styles.statsContainer}>
                <Text style={styles.statsHeader}>Learn</Text>
                {ARTICLES.map((article) => (
                  <View key={article.id} style={styles.articleCard}>
                    <Text style={styles.articleTitle}>{article.title}</Text>
                    <Text style={styles.articleSummary}>{article.summary}</Text>
                    <TouchableOpacity
                      style={[styles.actionButton, { backgroundColor: "#1ABC9C", marginTop: 8 }]}
                      onPress={() => setSelectedArticleId(article.id)}
                    >
                      <Text style={styles.buttonText}>Read</Text>
                    </TouchableOpacity>
                  </View>
                ))}

                <TouchableOpacity
                  style={[styles.actionButton, { backgroundColor: "#111", marginTop: 16 }]}
                  onPress={() => setActivePage("home")}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>
              </View>
            )}
            </>
          )}
        </ScrollView>
      )}

      <ScreenTimePasscodeGate
        visible={isScreenTimeProtectedPage && needsScreenTimeUnlock}
        lockedPageLabel={screenTimeLockLabel}
        passcodeInput={screenTimeUnlockInput}
        onChangePasscode={setScreenTimeUnlockInput}
        onUnlock={unlockScreenTime}
        onBack={onScreenTimeGateBack}
        onOpenHelp={() => setScreenTimeHelpVisible(true)}
      />

      <ScreenTimeHelpSession
        visible={screenTimeHelpVisible}
        onClose={() => setScreenTimeHelpVisible(false)}
        settings={settingsDraft}
        bestStreak={insights.bestStreak}
        lockedPageLabel={screenTimeLockLabel}
        onRevealedPasscode={(pin) => setScreenTimeUnlockInput(pin)}
      />

      <ScreenTimePasscodeSetup
        visible={passcodeSetupVisible}
        pin={pendingPasscodePin}
        onCancel={() => {
          setPasscodeSetupVisible(false);
          setPendingPasscodePin("");
        }}
        onComplete={completePasscodeSetup}
      />

      <Modal
        visible={relapseModalVisible}
        animationType="fade"
        transparent={true}
        onRequestClose={closeRelapseModal}
      >
        <View style={styles.modalBackdrop}>
          <ScrollView contentContainerStyle={styles.modalScrollContent} keyboardShouldPersistTaps="handled">
            <View style={styles.modalCard}>
              <Text style={styles.modalTitle}>Mark fail</Text>
              <Text style={styles.modalSubtitle}>
                {relapseDateKey ? `Date: ${relapseDateKey}` : "Date: today"}
                {openFailFromNotifications ? " · from Notifications" : ""}
              </Text>

              <FailTriggerPicker
                triggers={savedTriggers}
                value={relapseTrigger}
                onChange={setRelapseTrigger}
              />

              <Text style={styles.modalLabel}>Time of day</Text>
            <View style={styles.chipRow}>
              {["Morning", "Afternoon", "Evening", "Night"].map((opt) => {
                const selected = relapseTimeOfDay === opt;
                return (
                  <Pressable
                    key={opt}
                    onPress={() => setRelapseTimeOfDay(opt)}
                    style={[styles.chip, selected && styles.chipSelected]}
                  >
                    <Text style={[styles.chipText, selected && styles.chipTextSelected]}>
                      {opt}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <Text style={styles.modalLabel}>Mood (1–5)</Text>
            <View style={styles.chipRow}>
              {[1, 2, 3, 4, 5].map((n) => {
                const selected = relapseMood === n;
                return (
                  <Pressable
                    key={n}
                    onPress={() => setRelapseMood(n)}
                    style={[styles.chip, selected && styles.chipSelected]}
                  >
                    <Text style={[styles.chipText, selected && styles.chipTextSelected]}>
                      {n}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonSecondary]}
                onPress={closeRelapseModal}
              >
                <Text style={styles.modalButtonSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonPrimary]}
                onPress={saveRelapse}
              >
                <Text style={styles.modalButtonPrimaryText}>Save</Text>
              </TouchableOpacity>
            </View>
            </View>
          </ScrollView>
        </View>
      </Modal>

      <Modal
        visible={dayDetailsVisible}
        animationType="fade"
        transparent={true}
        onRequestClose={closeDayDetails}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Day details</Text>
            <Text style={styles.modalSubtitle}>
              {dayDetailsDateKey ? `Date: ${dayDetailsDateKey}` : "Date: today"}
            </Text>

            <Text style={styles.modalLabel}>Today’s note</Text>
            <TextInput
              value={dayNoteDraft}
              onChangeText={setDayNoteDraft}
              placeholder="Write anything you want to remember..."
              placeholderTextColor="#9AA0A6"
              style={[styles.modalInput, styles.noteInput]}
              multiline={true}
              maxLength={2000}
            />

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonSecondary]}
                onPress={closeDayDetails}
              >
                <Text style={styles.modalButtonSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonPrimary]}
                onPress={() => {
                  const key = dayDetailsDateKey ?? getTodayKey();
                  closeDayDetails();
                  handleSuccessMark(key, dayNoteDraft);
                }}
              >
                <Text style={styles.modalButtonPrimaryText}>Done fully</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonPrimary, { backgroundColor: PARTIAL_COLOR }]}
                onPress={() => {
                  const key = dayDetailsDateKey ?? getTodayKey();
                  closeDayDetails();
                  handlePartialMark(key, dayNoteDraft);
                }}
              >
                <Text style={styles.modalButtonPrimaryText}>Done partially</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonSecondary]}
                onPress={() => {
                  const key = dayDetailsDateKey ?? getTodayKey();
                  closeDayDetails();
                  handleMissedMark(key, dayNoteDraft);
                }}
              >
                <Text style={styles.modalButtonSecondaryText}>Missed</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonPrimary]}
                onPress={() => {
                  const key = dayDetailsDateKey ?? getTodayKey();
                  closeDayDetails();
                  openRelapseModal(key, dayNoteDraft);
                }}
              >
                <Text style={styles.modalButtonPrimaryText}>Relapse…</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonPrimary]}
                onPress={saveDayNote}
              >
                <Text style={styles.modalButtonPrimaryText}>Save note</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={urgeModalVisible}
        animationType="fade"
        transparent={true}
        onRequestClose={closeUrgeModal}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Panic Button</Text>
            <Text style={styles.modalSubtitle}>
              Just 60 seconds. Breathe slowly, relax your shoulders, and let the urge pass.
            </Text>

            <View style={styles.urgeTimerCircle}>
              <Text style={styles.urgeTimerText}>{formatSeconds(urgeSecondsLeft)}</Text>
            </View>

            <Text style={styles.urgeMessage}>
              You don’t have to act on it. Urges peak and fade — you’re training your brain every
              time you wait.
            </Text>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonSecondary]}
                onPress={closeUrgeModal}
              >
                <Text style={styles.modalButtonSecondaryText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={successOptionsVisible}
        animationType="fade"
        transparent={true}
        onRequestClose={closeSuccessOptions}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Mark today</Text>
            <Text style={styles.modalSubtitle}>Choose how it went.</Text>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonSecondary]}
                onPress={closeSuccessOptions}
              >
                <Text style={styles.modalButtonSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonPrimary]}
                onPress={() => {
                  const key = successOptionsDateKey ?? getTodayKey();
                  closeSuccessOptions();
                  handleSuccessMark(key);
                }}
              >
                <Text style={styles.modalButtonPrimaryText}>Done fully</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonPrimary, { backgroundColor: PARTIAL_COLOR }]}
                onPress={() => {
                  const key = successOptionsDateKey ?? getTodayKey();
                  closeSuccessOptions();
                  handlePartialMark(key);
                }}
              >
                <Text style={styles.modalButtonPrimaryText}>Done partially</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {milestoneToast.visible ? (
        <View pointerEvents="box-none" style={styles.toastOverlay}>
          <View pointerEvents="auto" style={styles.toastCard}>
            <Text style={styles.toastText}>{milestoneToast.message}</Text>
            <TouchableOpacity
              style={[styles.modalButton, styles.modalButtonSecondary, styles.toastDismissButton]}
              onPress={dismissMilestoneToast}
            >
              <Text style={styles.modalButtonSecondaryText}>Dismiss</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}
      {screenTimeEditPasscodeModal}
    </>
  );
}

const styles = StyleSheet.create({
  osStyleHint: { fontSize: 13, color: "#374151", lineHeight: 18, marginBottom: 12 },
  settingsInlineTitle: { fontSize: 18, fontWeight: "800", color: "#111", marginBottom: 10, textAlign: "center" },
  container: {
    flex: 1,
    padding: 10,
    backgroundColor: "#FDFDFD",
  },
  containerHub: {
    backgroundColor: "#7EE2E2",
    padding: 0,
  },
  containerFull: Platform.select({
    web: { flex: 1, padding: 0, height: "100vh", minHeight: "100vh" },
    default: { flex: 1, padding: 0 },
  }),
  containerHubContent: {
    flexGrow: 1,
  },
  title: {
    fontSize: 22,
    textAlign: "center",
    marginVertical: 15,
    fontWeight: "bold",
    color: "#1ABC9C",
  },
  actionButton: {
    padding: 16,
    borderRadius: 10,
    marginVertical: 8,
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    textAlign: "center",
    fontWeight: "bold",
  },
  statsContainer: {
    marginTop: 20,
  },
  statsHeader: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#1ABC9C",
    marginBottom: 10,
  },
  statsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  statsCard: {
    flex: 1,
    backgroundColor: "#F7F7F7",
    padding: 15,
    borderRadius: 10,
    margin: 5,
    alignItems: "center",
  },
  statsLabel: {
    fontSize: 14,
    marginBottom: 5,
    color: "#555",
  },
  statsValue: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1ABC9C",
  },
  statsSubvalue: {
    marginTop: 4,
    color: "#666",
    fontSize: 12,
  },
  progressWrap: {
    marginTop: 10,
    marginBottom: 8,
  },
  progressLabel: {
    textAlign: "center",
    color: "#555",
    marginBottom: 8,
    fontWeight: "600",
  },
  progressTrack: {
    height: 10,
    backgroundColor: "#E5E7EB",
    borderRadius: 999,
    overflow: "hidden",
    marginHorizontal: 6,
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#1ABC9C",
  },
  reminderText: {
    textAlign: "center",
    color: "#555",
    marginBottom: 30,
    fontWeight: "600",
  },
  quoteIntroContainer: {
    marginTop: 40,
    marginHorizontal: 16,
    padding: 20,
    borderRadius: 14,
    backgroundColor: "#F7F7F7",
  },
  quoteIntroText: {
    fontSize: 18,
    lineHeight: 24,
    color: "#111",
    textAlign: "center",
    marginBottom: 12,
  },
  quoteIntroAuthor: {
    textAlign: "center",
    color: "#555",
    fontWeight: "600",
  },
  quoteOfDayContainer: {
    marginBottom: 10,
    padding: 14,
    borderRadius: 12,
    backgroundColor: "#F7F7F7",
  },
  quoteOfDayText: {
    color: "#111",
    lineHeight: 20,
    marginBottom: 6,
  },
  quoteOfDayAuthor: {
    color: "#555",
    fontWeight: "600",
  },
  articleCard: {
    backgroundColor: "#F7F7F7",
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  articleTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#111",
    marginBottom: 6,
  },
  articleSummary: {
    color: "#555",
    lineHeight: 18,
  },
  articleBody: {
    color: "#111",
    lineHeight: 20,
    marginBottom: 16,
  },
  blockedAttemptsHeaderRow: {
    marginTop: 10,
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  blockedAttemptsButton: {
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  toastOverlay: {
    position: "absolute",
    top: 12,
    left: 12,
    right: 12,
    zIndex: 9999,
  },
  toastCard: {
    backgroundColor: "#111",
    borderRadius: 14,
    padding: 14,
  },
  toastText: {
    color: "#fff",
    lineHeight: 20,
    fontWeight: "600",
  },
  toastDismissButton: {
    alignSelf: "flex-end",
    marginTop: 10,
  },
  pickerRow: {
    justifyContent: "center",
  },
  pickerRowText: {
    color: "#111",
    fontWeight: "600",
  },
  streakText: {
    marginTop: 20,
    textAlign: "center",
    fontSize: 16,
    fontWeight: "bold",
    color: "#1ABC9C",
    marginBottom: 8,
  },
  attemptsHint: {
    textAlign: "center",
    fontSize: 12,
    color: "#64748B",
    marginBottom: 24,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    padding: 16,
  },
  modalScrollContent: {
    flexGrow: 1,
    justifyContent: "center",
  },
  modalCard: {
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#111",
  },
  modalSubtitle: {
    marginTop: 4,
    marginBottom: 12,
    color: "#555",
  },
  modalLabel: {
    marginTop: 10,
    marginBottom: 6,
    fontWeight: "bold",
    color: "#333",
  },
  modalInput: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: "#111",
    backgroundColor: "#FAFAFA",
  },
  noteInput: {
    minHeight: 110,
    textAlignVertical: "top",
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  chip: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#fff",
  },
  chipSelected: {
    borderColor: "#1ABC9C",
    backgroundColor: "rgba(26, 188, 156, 0.12)",
  },
  chipText: {
    color: "#333",
    fontWeight: "600",
  },
  chipTextSelected: {
    color: "#148F77",
  },
  chipDangerSelected: {
    borderColor: "#E74C3C",
    backgroundColor: "rgba(231, 76, 60, 0.12)",
  },
  chipDangerTextSelected: {
    color: "#B03A2E",
  },
  categoryRuleRow: {
    marginTop: 8,
    marginBottom: 6,
  },
  categoryRuleName: {
    color: "#333",
    fontWeight: "600",
    marginBottom: 6,
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
  },
  modalButton: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
  },
  modalButtonPrimary: {
    backgroundColor: "#1ABC9C",
  },
  modalButtonPrimaryText: {
    color: "#fff",
    fontWeight: "bold",
  },
  modalButtonSecondary: {
    backgroundColor: "#F3F4F6",
  },
  modalButtonSecondaryText: {
    color: "#111",
    fontWeight: "bold",
  },
  urgeTimerCircle: {
    alignSelf: "center",
    width: 140,
    height: 140,
    borderRadius: 70,
    borderWidth: 4,
    borderColor: "#6C5CE7",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 8,
    marginBottom: 12,
  },
  urgeTimerText: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#111",
  },
  urgeMessage: {
    color: "#333",
    lineHeight: 20,
  },
  androidVpnButtonRow: {
    flexDirection: "row",
    marginTop: 10,
    alignItems: "center",
  },
  screenTimeLockScreen: {
    flex: 1,
    backgroundColor: "#fff",
    justifyContent: "center",
    paddingHorizontal: 24,
    paddingTop: 48,
  },
  screenTimeDisclaimer: {
    color: "#666",
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 10,
  },
  settingsSwitchRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 8,
    marginBottom: 12,
  },
  settingsSwitchCopy: { flex: 1 },
  ruleTestResultText: {
    marginTop: 8,
    color: "#333",
    fontWeight: "600",
  },
  ruleTestSuggestionText: {
    marginTop: 4,
    color: "#555",
    fontSize: 12,
  },
});
